import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { QuickstartTheme, getResponsiveTextSize } from '../types/theme';
import { useIsLandscape } from '../utils/responsiveHooks';
import { ThemeLogo } from './ThemeLogo';
import {
  AnimationConfig,
  ANIMATION_PRESETS,
  ANIMATIONS_ENABLED,
  getInitialState,
  getAnimateState,
  getExitState,
  getTransition,
} from '../config/animations';

/**
 * Convert hex color to rgba with opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Hook to detect if device is mobile
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind's md breakpoint
    };

    // Check on mount
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

/**
 * Shared card component for all quickstart screens
 * Themed based on game configuration
 */
interface QuickstartCardProps {
  theme: QuickstartTheme;
  children: React.ReactNode;
  className?: string;
  animation?: AnimationConfig;
  showHeader?: boolean;
  badgeOverride?: string; // Override the release date badge with custom text (e.g., "SNEAK PEEK")
  hideBadge?: boolean; // Hide the release date badge completely
  hideFooter?: boolean; // Hide the "Powered by Playcast" footer logo
  hideStudioLogo?: boolean; // Hide the studio logo (e.g., DeadDrop)
}

export const QuickstartCard = ({
  theme,
  children,
  className = '',
  animation = ANIMATION_PRESETS.cardDefault,
  showHeader = true,
  badgeOverride,
  hideBadge = false,
  hideFooter = false,
  hideStudioLogo = false,
}: QuickstartCardProps) => {
  // Use 'none' animation if globally disabled
  const effectiveAnimation = ANIMATIONS_ENABLED ? animation : ANIMATION_PRESETS.none;

  // Detect if mobile and landscape
  const isMobile = useIsMobile();
  const isLandscape = useIsLandscape();

  // Note: Modal opacity is consistent across all devices (mobile and desktop)
  // This is intentional - we want modals to be visible on mobile, not transparent
  // Previously modals were fully transparent on mobile (opacity: 0), but this was changed
  // to ensure consistent visibility across all devices
  const modalOpacity = theme.modalBackgroundOpacity ?? 0.95;
  const modalBackground = hexToRgba(theme.modalBackgroundColor, modalOpacity);

  // Adjust padding and sizing for landscape mode
  const contentPadding = isLandscape ? 'px-4 py-2' : 'px-8 py-4';
  const maxWidth = isLandscape ? 'max-w-2xl' : 'max-w-md';

  return (
    <motion.div
      className={`relative mx-auto flex w-full ${maxWidth} flex-col items-center rounded-2xl ${isMobile ? '' : 'shadow-lg'} ${className}`}
      style={{
        backgroundColor: modalBackground,
      }}
      initial={getInitialState(effectiveAnimation)}
      animate={getAnimateState(effectiveAnimation)}
      exit={getExitState(effectiveAnimation)}
      transition={getTransition(effectiveAnimation)}
    >
      {showHeader && (
        <>
          {/* Studio Logo - flush to top */}
          {!hideStudioLogo && (
            <ThemeLogo
              kind='studio'
              theme={theme}
              isLandscape={isLandscape}
              className={isLandscape ? 'mt-2' : 'mt-4'}
              loading='eager'
            />
          )}

          {/* Game Logo or Game Name */}
          {theme.gameLogo ? (
            <ThemeLogo
              kind='game'
              theme={theme}
              isLandscape={isLandscape}
              className={isLandscape ? 'mt-2' : 'mt-12'}
              loading='eager'
            />
          ) : (
            <h1
              className={`font-bold ${isLandscape ? 'mt-2' : 'mt-12'} ${getResponsiveTextSize('3xl', isLandscape)}`}
              style={{ color: theme.primaryColor }}
            >
              {theme.gameName}
            </h1>
          )}

          {/* Badge - Shows Badge Override or Release Date if provided */}
          {!hideBadge && (badgeOverride || theme.releaseDate) && (
            <div
              className={`rounded px-3 py-1 text-center font-["Inter"] font-semibold ${isLandscape ? 'mb-2 mt-2' : 'mb-6 mt-8'} ${getResponsiveTextSize('2xl', isLandscape)}`}
              style={{
                color: badgeOverride
                  ? theme.badgeOverrideTextColor || theme.releaseDateTextColor || theme.modalBackgroundColor
                  : theme.releaseDateTextColor || theme.modalBackgroundColor,
              }}
            >
              {badgeOverride ? (
                <div>{badgeOverride}</div>
              ) : (
                <>
                  <div>COMING:</div>
                  <div>{theme.releaseDate}</div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Content - with padding */}
      <div className={`w-full ${contentPadding}`}>{children}</div>

      {/* Powered by Playcast - flush to bottom */}
      {!hideFooter && (
        <ThemeLogo
          kind='poweredBy'
          theme={theme}
          isLandscape={isLandscape}
          className={isLandscape ? 'mb-2 mt-4' : 'mb-4 mt-12'}
        />
      )}
    </motion.div>
  );
};

